import { ApiBaseService } from './ApiBaseService';

import type { ApiResponse } from '../types/api';
import type { Namespace } from '../types/namespace';
import type { NamespaceDimension } from '../types/namespace-dimension';
import type { VectorEmbeddingModel } from '../types/vector';

interface VectorApiParams {
  id?: string;
  nameSpace: string;
  spaceId?: string;
}

interface VectorListAllResponse extends ApiResponse<Array<Namespace>> {}

interface VectorSearchRequest {
  body: {
    prompt: string;
    model: string;
    topK: number;
    maxTokenModel: number;
    isNERSearch: boolean;
    embeddingModelName: string;
    returnRaw: boolean;
    filters?: Record<string, any> | null;
    uploadNewContent?: string[];
  };
  params: VectorApiParams;
}

interface VectorSearchResponse
  extends ApiResponse<{
    text: string;
    linesWhereQuestionWasFound: Array<{
      dimensions: Array<string>;
      lines: string;
      pageContent: string;
    }>;
  }> {}

interface VectorUpdateRequest {
  body: Partial<{
    newPodName: string;
    searchUsingDimensions: boolean;
  }>;
  params: VectorApiParams;
}

interface VectorAddDimensionsRequest {
  body: {
    filesIds: Array<string>;
    dimensions: Array<Partial<NamespaceDimension>>;
    embeddingModelName: VectorEmbeddingModel;
  };
  params: VectorApiParams;
}

interface VectorUpdateDimensionsRequest {
  body: {
    filesIds: Array<string>;
    dimensions: Array<Partial<NamespaceDimension>>;
    embeddingModelName: VectorEmbeddingModel;
  };
  params: VectorApiParams;
}

interface VectorDeleteDimensionsRequest {
  body: {
    filesIds: Array<string>;
    dimensions: Array<{ id: string; title: string }>;
    embeddingModelName: VectorEmbeddingModel;
  };
  params: VectorApiParams;
}

class VectorService extends ApiBaseService {
  static pathName = '/vector';

  static listAll = async () => {
    return await ApiBaseService.get<VectorListAllResponse>(
      `${this.pathName}/all`,
    );
  };

  static search = async ({ body, params }: VectorSearchRequest) => {
    return await ApiBaseService.post<VectorSearchResponse>(
      `${this.pathName}/search`,
      body,
      {
        params,
      },
    );
  };

  static updateNamespace = async ({ body, params }: VectorUpdateRequest) => {
    return await ApiBaseService.put(`${this.pathName}`, body, { params });
  };

  static addDimensions = async ({
    body,
    params,
  }: VectorAddDimensionsRequest) => {
    return await ApiBaseService.post(`${this.pathName}/dimension/add`, body, {
      params,
    });
  };

  static updateDimensions = async ({
    body,
    params,
  }: VectorUpdateDimensionsRequest) => {
    return await ApiBaseService.put(`${this.pathName}/dimension/update`, body, {
      params,
    });
  };

  static deleteDimensions = async ({
    body,
    params,
  }: VectorDeleteDimensionsRequest) => {
    return await ApiBaseService.delete(`${this.pathName}/dimension/delete`, {
      data: body,
      params,
    });
  };
}

export {
  VectorService,
  type VectorApiParams,
  type VectorListAllResponse,
  type VectorUpdateRequest,
  type VectorAddDimensionsRequest,
  type VectorUpdateDimensionsRequest,
  type VectorDeleteDimensionsRequest,
};
